import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    useEffect
} from 'react';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import {
    Info,
    TriangleAlert
} from 'lucide-react';
import {
    Link, useParams
} from 'react-router-dom';

export default function CollectionForm({
    form,
    setForm,
    hasMilvusCollection,
    vectorIndex,
    initialData = null
}) {

    const {
        oragID,
        subOragID
    } = useParams();

    const VECTOR_STORE_TYPES = ['MongoDB', 'Milvus'];
    const DIMENSIONS_TYPES = {
        MongoDB: [1024],
        Milvus: [1024, 256]
    };

    useEffect(() => {
        if (initialData && DIMENSIONS_TYPES[form.vectorStoreType].includes(initialData.dimensions)) {
            setForm(prev => ({
                ...prev,
                dimensions: initialData.dimensions
            }));
        } else {
            setForm(prev => ({
                ...prev,
                dimensions: DIMENSIONS_TYPES[form.vectorStoreType] && DIMENSIONS_TYPES[form.vectorStoreType][0]
            }));
        }
    }, [form.vectorStoreType]);

    return (
        <div className="space-y-8 pt-4">
            <div className="space-y-3">
                {/* check if neither vector index and hasMilvusCollection is true then display alert for user to activate one of them at least */}
                {!vectorIndex && !hasMilvusCollection && (
                    <div className="bg-orange-500/10 border border-orange-500/20 text-orange-500 p-4 rounded-lg flex gap-3 items-start animate-in fade-in slide-in-from-top-2 duration-300">
                        <TriangleAlert className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div className='flex flex-col gap-2'>
                            <p> Please activate either Milvus or MongoDB vector index to create a collection </p>
                            <div className='flex flex-col gap-2'>
                                <Link className='text-blue-500' to={`/organizations/${oragID}/sub/${subOragID}/settings`}> activate MongoDB </Link>
                                <Link className='text-blue-500' to={`/organizations/${oragID}/integrations`}> activate Milvus </Link>
                            </div>
                        </div>

                    </div>
                )}
                <div>
                    <Label htmlFor='collection-name'> Name </Label>
                    <Input type='text' id='collection-name' value={form.name}
                        onChange={(e) => setForm(prev => ({
                            ...prev,
                            name: e.target.value
                        }))}
                    />
                </div>
                <div>
                    <Label htmlFor='vector-store-type'> Vector store type </Label>
                    <Select onValueChange={(value) => setForm(prev => ({
                        ...prev,
                        vectorStoreType: value
                    }))} id="vector-store-type" value={form.vectorStoreType}>
                        <SelectTrigger className="w-full capitalize">
                            <SelectValue className="capitalize" placeholder="Select vector store type" />
                        </SelectTrigger>
                        <SelectContent>
                            {VECTOR_STORE_TYPES.map(item => (
                                <VectorStoreType
                                    key={item}
                                    item={item}
                                    hasMilvusCollection={hasMilvusCollection}
                                    vectorIndex={vectorIndex}
                                />
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label htmlFor='dimensions'> Dimensions </Label>
                    {DIMENSIONS_TYPES[form.vectorStoreType]?.length > 1 ? (
                        <Select onValueChange={(value) => setForm(prev => ({
                            ...prev,
                            dimensions: value
                        }))} id="dimensions" value={form.dimensions}>
                            <SelectTrigger className="w-full capitalize">
                                <SelectValue className="capitalize" placeholder="Select collection dimensions" />
                            </SelectTrigger>
                            <SelectContent>
                                {DIMENSIONS_TYPES[form.vectorStoreType].map(item => (
                                    <SelectItem className='capitalize' key={item} value={item}>{item} </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <span className="block text-sm text-muted-foreground">{DIMENSIONS_TYPES[form.vectorStoreType]?.[0] ?? 1024}</span>
                    )}
                </div>
                <div>
                    <Label htmlFor='embedding-model'> Embedding Model </Label>
                    <span className="block text-sm text-muted-foreground">Jina</span>
                </div>
                <div>
                    <Label htmlFor='chunking-strategy'> Chunking Strategy </Label>
                    <span className="block text-sm text-muted-foreground">By Page</span>
                </div>
                <div className="border-t pt-4 flex items-center justify-between">
                    <div>
                        <Label className="text-[0.85rem] font-semibold mb-0.5">PDF Page Images</Label>
                        <p className="text-xs text-muted-foreground">
                            Extract each page of scanned/image-heavy PDFs as PNG images during processing.
                        </p>
                    </div>
                    <Switch
                        id="pdf-page-images"
                        checked={!!form.pdfPageImages}
                        onCheckedChange={(checked) => setForm(prev => ({
                            ...prev,
                            pdfPageImages: checked
                        }))}
                    />
                </div>
            </div>
        </div>
    );
}

const VectorStoreType = ({
    item,
    hasMilvusCollection,
    vectorIndex
}) => {

    const checkStoreTypeDisabled = (item) => {
        if (item === 'MongoDB' && !vectorIndex) return {
            disabled: true,
            reason: 'MongoDB vector index is not activated yet'
        };

        if (item === 'Milvus' && !hasMilvusCollection) return {
            disabled: true,
            reason: 'Milvus collection not activated'
        };

        return {
            disabled: false,
            reason: ''
        };
    };

    const isOptionsDisabled = checkStoreTypeDisabled(item);

    return (
        <TooltipProvider>
            <Tooltip className='z-[1000]'>
                <TooltipTrigger asChild>
                    <div className='relative flex items-center gap-1'>
                        <SelectItem disabled={isOptionsDisabled.disabled} className='capitalize' value={item}>{item}</SelectItem>
                        {isOptionsDisabled.disabled && <Info size={19} className='absolute top-1 left-2' />}
                    </div>
                </TooltipTrigger>
                {isOptionsDisabled.disabled && (
                    <TooltipContent className="bg-gray-900 text-white w-fit z-[1000]">
                        <p>{isOptionsDisabled.reason}</p>
                    </TooltipContent>
                )}
            </Tooltip>
        </TooltipProvider>
    );
};